import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  ticketsApi,
  type CreateTicketInput,
  type EscalateDeviationInput,
  type LinkedRecordInput,
  type ResolveTicketInput,
  type TicketDetail,
  type UpdateTicketInput,
} from '@/api/ticketsApi'
import { toApiParams, type QueueFilters } from '@/lib/queueFilters'
import { TICKET_COUNTS_KEY } from '@/hooks/useTicketCounts'

export const PAGE_SIZE = 25

export const ticketKey = (id: string) => ['ticket', id] as const

/** One page of the queue. Keeps the previous page on screen while the next loads. */
export function useTicketList(filters: QueueFilters, cursor: string | null) {
  const params = toApiParams(filters, { limit: PAGE_SIZE, cursor })
  return useQuery({
    queryKey: ['tickets', params.toString()],
    queryFn: () => ticketsApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKey(id),
    queryFn: () => ticketsApi.get(id),
    enabled: Boolean(id),
    staleTime: 10_000,
  })
}

/** After any ticket write: store the fresh detail and mark lists, counts and the audit trail stale. */
export function applyTicketWrite(queryClient: QueryClient, routeId: string, detail: TicketDetail) {
  queryClient.setQueryData(ticketKey(routeId), detail)
  void queryClient.invalidateQueries({ queryKey: ['tickets'] })
  void queryClient.invalidateQueries({ queryKey: TICKET_COUNTS_KEY })
  void queryClient.invalidateQueries({ queryKey: ['ticket-audit', routeId] })
  void queryClient.invalidateQueries({ queryKey: ['ticket-messages', routeId] })
}

/**
 * The SLA policy a new ticket would get (`GET /sla-policies/applicable`, tickets.create) — the intake forms' hint.
 * Disabled until priority, customer and product are all known.
 */
export function useApplicableSlaPolicy(q: { priority: string; customerId: string; productId: string }, enabled = true) {
  const ready = enabled && Boolean(q.priority && q.customerId && q.productId)
  return useQuery({
    queryKey: ['sla-policies', 'applicable', q.priority, q.customerId, q.productId],
    queryFn: () => ticketsApi.applicableSlaPolicy(q),
    enabled: ready,
    staleTime: 60_000,
  })
}

export function useCreateTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTicketInput) => ticketsApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
      void queryClient.invalidateQueries({ queryKey: TICKET_COUNTS_KEY })
    },
  })
}

/** PATCH with If-Match. The caller handles a 409 (refetch + notice). */
export function useUpdateTicket(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ version, input }: { version: number; input: UpdateTicketInput }) =>
      ticketsApi.update(routeId, version, input),
    onSuccess: (detail) => applyTicketWrite(queryClient, routeId, detail),
  })
}

export function useResolveTicket(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ResolveTicketInput) => ticketsApi.resolve(routeId, input),
    onSuccess: (detail) => applyTicketWrite(queryClient, routeId, detail),
  })
}

export function useReopenTicket(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => ticketsApi.reopen(routeId, reason),
    onSuccess: (detail) => applyTicketWrite(queryClient, routeId, detail),
  })
}

export function useLinkRecord(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LinkedRecordInput) => ticketsApi.linkRecord(routeId, input),
    onSuccess: (detail) => applyTicketWrite(queryClient, routeId, detail),
  })
}

export function useEscalateDeviation(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: EscalateDeviationInput) => ticketsApi.escalateDeviation(routeId, input),
    onSuccess: (detail) => applyTicketWrite(queryClient, routeId, detail),
  })
}

/** Looks a ticket up by key or number (duplicate picker). */
export function useTicketLookup() {
  const queryClient = useQueryClient()
  return (keyOrNumber: string) =>
    queryClient.fetchQuery({ queryKey: ticketKey(keyOrNumber), queryFn: () => ticketsApi.get(keyOrNumber), staleTime: 30_000 })
}

export function useTicketAudit(routeId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ticket-audit', routeId],
    queryFn: () => ticketsApi.audit(routeId),
    enabled: enabled && Boolean(routeId),
  })
}

export function useVerifyAudit(routeId: string) {
  return useMutation({ mutationFn: () => ticketsApi.verifyAudit(routeId) })
}

export function useExportThread(routeId: string) {
  return useMutation({ mutationFn: (audience: 'customer' | 'internal') => ticketsApi.exportThread(routeId, audience) })
}
